<script setup lang="ts">
import type { ILoaderProps } from "./VLoader.types";
import { computed } from "vue";
import VText from "../VText/VText.vue";

const props = withDefaults(defineProps<ILoaderProps>(), {
  color: "primary",
  size: "xs",
});

const isLargeSize = computed(() => props.size === "l");
</script>

<template>
  <div class="pl-loader-wrapper">
    <div
      class="pl-loader"
      :class="[
        `pl-loader_${isLargeSize ? 'project' : color}`,
        `pl-loader_${size}`,
      ]"
    >
      <template v-if="isLargeSize">
        <svg viewBox="0 0 66 66">
          <circle
            fill="none"
            stroke-width="6"
            stroke-linecap="round"
            cx="33"
            cy="33"
            r="30"
          />
        </svg>
        <svg viewBox="0 0 66 66">
          <circle
            fill="none"
            stroke-width="6"
            stroke-linecap="round"
            cx="33"
            cy="33"
            r="30"
          />
        </svg>
        <svg viewBox="0 0 66 66">
          <circle
            fill="none"
            stroke-width="6"
            stroke-linecap="round"
            cx="33"
            cy="33"
            r="30"
          />
        </svg>
      </template>
      <template v-else>
        <svg viewBox="0 0 66 66">
          <circle
            fill="none"
            stroke-width="6"
            stroke-linecap="round"
            cx="33"
            cy="33"
            r="30"
          />
        </svg>
      </template>

      <svg v-show="isLargeSize" viewBox="0 0 66 66">
        <circle
          fill="none"
          stroke-width="6"
          stroke-linecap="round"
          cx="33"
          cy="33"
          r="30"
        />
      </svg>
    </div>
    <VText
      v-if="isLargeSize && title"
      class="pl-loader-title"
      font="body-large-accent"
      >{{ title }}</VText
    >
  </div>
</template>

<style scoped lang="postcss">
.pl-loader-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
}

.pl-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &_xxs {
    width: 16px;
    height: 16px;
  }

  &_xs {
    width: 24px;
    height: 24px;
  }

  &_s {
    width: 32px;
    height: 32px;
  }

  &_m {
    height: 40px;
    width: 40px;
  }

  &_l {
    height: 83px;
    width: 83px;
  }

  &_primary {
    color: var(--pl-icon-primary);
  }

  &_invert {
    color: var(--pl-icon-invert-primary);
  }

  &_project {
    color: var(--pl-loader-primary);
  }

  &-title {
    margin-top: var(--pl-unit-x8);
    text-align: center;
  }

  &-wrap {
    position: relative;
    height: 70%;
    width: 70%;
  }

  svg {
    animation: rotate 1.5s linear infinite;
    height: 70%;
    width: 70%;
    position: absolute;
    z-index: var(--pl-z-index-level2);

    circle {
      stroke-dasharray: 200;
      stroke-dashoffset: 0;
      transform-origin: center;
      animation: dash 1.5s ease-in-out infinite 0s;
      stroke-linecap: round;
      fill: none;
      stroke-width: 6;
      stroke: currentcolor;
    }

    &:nth-child(2) {
      z-index: var(--pl-z-index-level1);

      circle {
        color: var(--pl-loader-secondary);
        animation-timing-function: cubic-bezier(0.41, -0.01, 0.69, 0.51);
      }
    }

    &:nth-child(3) {
      z-index: var(--pl-z-index-level0);

      circle {
        color: var(--pl-loader-tertiary);
        animation-timing-function: cubic-bezier(1, 0, 0.69, 0.51);
      }
    }
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(270deg);
  }
}

@keyframes dash {
  0% {
    stroke-dashoffset: 200;
  }

  50% {
    stroke-dashoffset: 50;
    transform: rotate(135deg);
  }

  100% {
    stroke-dashoffset: 200;
    transform: rotate(450deg);
  }
}
</style>
